import React, { useEffect, useState, useContext, useRef } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { Container, Dimmer, Segment, Loader, Icon } from 'semantic-ui-react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'

import StoryItem from '../Stories/StoryItem'
import { db } from '../firebaseConfig'
import { EnvironmentContext } from '../Context/EnvironmentContext'
import DemoPreview from './DemoPreview'
import InteractiveDemo from './InteractiveDemo'

import './HomePage.css'
import './ListStories.css'

const HomePage = () => {
    const { t, i18n } = useTranslation()
    const env = useContext(EnvironmentContext)
    const navigate = useNavigate()
    
    const [banner, setBanner] = useState(null)
    const [initialLoadingFablia, setInitialLoadingFablia] = useState(true)
    const [storiesCreatedByFablia, setStoriesCreatedByFablia] = useState([])
    const [openFaqIndex, setOpenFaqIndex] = useState(null)
    const catalogueRef = useRef(null)
    const howItWorksRef = useRef(null)
    const demoRef = useRef(null)
    const faqRef = useRef(null)
    const finalCtaRef = useRef(null)
    
    const listOfIdsOfStoriesCreatedByFablia = [
        'Za2kgJ5eqkuBETwf1CMg',
        'l641kF9ywGPMEhEilQH3',
        'gKm8BR0dJGpzEStgbIwm',
        'rWaeigoal3uUk9aDsj8J',
    ]

    // Map i18n language codes to Firestore stored language labels
    const mapI18nToFirestoreLang = (lng) => {
        if (!lng) return 'francais'
        const code = String(lng).toLowerCase()
        const base = code.split('-')[0]
        const map = {
            fr: 'francais',
            en: 'english',
            es: 'spanish',
            de: 'deutsch',
        }
        return map[base] || map[code] || 'francais'
    }

    const [selectedLanguage, setSelectedLanguage] = useState(
        mapI18nToFirestoreLang(
            typeof navigator !== 'undefined'
                ? navigator.language
                : i18n?.language
        )
    )

    const fetchStoriesCreatedByFablia = async () => {
        setInitialLoadingFablia(true)
        const fetchedStories = []
        for (const id of listOfIdsOfStoriesCreatedByFablia) {
            const storyRef = doc(db, 'stories', id)
            const docSnap = await getDoc(storyRef)
            if (docSnap.exists()) {
                fetchedStories.push({ id: docSnap.id, ...docSnap.data() })
            } else {
                console.log('No such document!')
            }
        }
        setStoriesCreatedByFablia(fetchedStories)
        setInitialLoadingFablia(false)
    }

    useEffect(() => {
        const banners = [
            './Banners/20250303_110636_d7204dab-b2cf-4b9c-bb68-a36a395905de.jpg',
            './Banners/20250303_111142_050fe721-fb23-4b3b-b24f-5a26c72a142b.jpg',
            './Banners/20250303_112346_6728e024-67fa-4a57-9e1b-11abcb56a071.jpg',
            './Banners/20250303_112617_0953be47-9fd9-496e-9d3a-1566a38f1224.jpg',
            './Banners/20250303_113012_6e6c3206-7f5a-48a4-872d-6da65f0714b3.jpg',
            './Banners/20250303_142256_d57c448a-90ae-48ad-832f-b0de2cd143f2.jpg',
            './Banners/20260120_1836_Image Generation_simple_compose_01kfe7kv68fjr8wczfmq587afh.webp',
            './Banners/20260120_1356_Image Generation_simple_compose_01kfdqjxkjetj8w8pa0j8eqgep.webp',
            './Banners/20260120_1836_Image Generation_simple_compose_01kfe7kv67eef8v2823q36380c.webp',
            './Banners/20260120_1356_Image Generation_simple_compose_01kfdqjxkkfzf85xkcaad3jknw.webp',
        ]
        const randomBanner = banners[Math.floor(Math.random() * banners.length)]
        setBanner(randomBanner)
        
        // Force scroll to top on mount (prevents browser scroll restoration)
        window.scrollTo(0, 0)
    }, [])

    useEffect(() => {
        fetchStoriesCreatedByFablia()
    }, [env.user])

    const handleStartAdventure = () => {
        navigate('/SelectStory')
    }

    const handleCreateStories = () => {
        navigate(env.user ? '/Create' : '/CreateHomePage')
    }

    const scrollToNextSection = () => {
        howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const scrollToDemo = () => {
        demoRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const scrollToCatalogue = () => {
        catalogueRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const scrollToFaq = () => {
        faqRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const scrollToFinalCta = () => {
        finalCtaRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const faqItems = [
        {
            question: t('faqQuestion1'),
            answer: t('faqAnswer1')
        },
        {
            question: t('faqQuestion2'),
            answer: t('faqAnswer2')
        },
        {
            question: t('faqQuestion3'),
            answer: t('faqAnswer3')
        },
        {
            question: t('faqQuestion4'),
            answer: t('faqAnswer4')
        },
        {
            question: t('faqQuestion5'),
            answer: t('faqAnswer5')
        },
        {
            question: t('faqQuestion6'),
            answer: t('faqAnswer6')
        }
    ]

    const howItWorksSteps = [
        {
            icon: 'book',
            title: t('howItWorksStep1Title'),
            description: t('howItWorksStep1Desc')
        },
        {
            icon: 'comments',
            title: t('howItWorksStep2Title'),
            description: t('howItWorksStep2Desc')
        },
        {
            icon: 'magic',
            title: t('howItWorksStep3Title'),
            description: t('howItWorksStep3Desc')
        }
    ]

    const filteredStories = selectedLanguage && selectedLanguage !== 'ALL'
        ? storiesCreatedByFablia.filter(
            (s) => String(s.parameters?.language || '').toLowerCase() === selectedLanguage
          )
        : storiesCreatedByFablia

    return (
        <div className="homepage">
            <Helmet>
                <title>Fablia - Vos choix. Votre histoire. L'IA leur donne vie.</title>
                <meta 
                    name="description" 
                    content="Vivez des aventures uniques où chaque décision change l'histoire. Romance, thriller, fantasy... L'IA s'adapte à vos choix. Gratuit, sans inscription." 
                />
                <link rel="canonical" href="https://fablia.fr" />
            </Helmet>

            {/* Hero Section */}
            <section className="hero-section">
                {banner && (
                    <div className="hero-background">
                        <img src={banner} className="hero-image" alt="Fablia Adventure" />
                        <div className="hero-overlay" />
                    </div>
                )}
                <div className="hero-split-container">
                    <div className="hero-content">
                        <h1 className="hero-title">
                            {t('heroTitle')}
                            <span className="hero-title-accent">
                                {t('heroTitleAccent')}
                            </span>
                        </h1>
                        <p className="hero-subtitle">
                            {t('heroSubtitle')}
                        </p>
                        <div className="hero-cta-container">
                            <button className="cta-primary" onClick={handleStartAdventure}>
                                <Icon name="play" />
                                {t('ctaPrimary')}
                            </button>
                            <span className="cta-subtext">{t('ctaSubtext')}</span>
                        </div>
                        <button className="cta-secondary" onClick={handleCreateStories}>
                            {t('ctaSecondary')} →
                        </button>
                    </div>
                    <div className="hero-demo-container">
                        <DemoPreview autoPlay={true} />
                    </div>
                </div>
                <button className="scroll-indicator" onClick={scrollToNextSection} aria-label="Scroll down">
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works-section" ref={howItWorksRef}>
                <Container>
                    <h2 className="section-heading">{t('howItWorksTitle')}</h2>
                    <div className="steps-container">
                        {howItWorksSteps.map((step, index) => (
                            <div className="step-card" key={index}>
                                <div className="step-number">{index + 1}</div>
                                <div className="step-icon">
                                    <Icon name={step.icon} size="big" />
                                </div>
                                <h3 className="step-title">{step.title}</h3>
                                <p className="step-description">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </Container>
                <button className="scroll-indicator section-scroll" onClick={scrollToDemo} aria-label="Scroll to demo">
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Interactive Demo Section */}
            <section className="interactive-demo-section" ref={demoRef}>
                <Container>
                    <h2 className="section-heading">{t('demo.sectionTitle')}</h2>
                    <p className="section-subheading">
                        {t('demo.sectionSubtitle')}
                    </p>
                    <InteractiveDemo storyId="Za2kgJ5eqkuBETwf1CMg" />
                </Container>
                <button className="scroll-indicator section-scroll" onClick={scrollToCatalogue} aria-label="Scroll to catalogue">
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Catalogue Section */}
            <section className="catalogue-section" ref={catalogueRef}>
                <Container>
                    <h2 className="section-heading">{t('catalogueTitle')}</h2>
                    <p className="section-subheading">
                        {t('catalogueSubtitle')}
                    </p>
                    <Segment basic className="stories-carousel">
                        <button
                            className="carousel-arrow left"
                            onClick={() => {
                                document.querySelector('.stories-track')?.scrollBy({
                                    left: -300,
                                    behavior: 'smooth',
                                })
                            }}
                            aria-label="Previous stories"
                        >
                            <Icon name="chevron left" />
                        </button>

                        <div className="stories-track">
                            {initialLoadingFablia ? (
                                <Dimmer active inverted>
                                    <Loader>{t('Loading')}</Loader>
                                </Dimmer>
                            ) : (
                                filteredStories.map((story) => (
                                    <StoryItem
                                        storyElement={story}
                                        fetchStoriesFunction={fetchStoriesCreatedByFablia}
                                        alreadyStarted={false}
                                        key={story.id + 5555}
                                        minimalView={true}
                                    />
                                ))
                            )}
                        </div>

                        <button
                            className="carousel-arrow right"
                            onClick={() => {
                                document.querySelector('.stories-track')?.scrollBy({
                                    left: 300,
                                    behavior: 'smooth',
                                })
                            }}
                            aria-label="Next stories"
                        >
                            <Icon name="chevron right" />
                        </button>
                    </Segment>
                    <div className="catalogue-cta">
                        <button className="cta-outline" onClick={() => navigate('/SelectStory')}>
                            {t('seeAllStories')}
                        </button>
                    </div>
                </Container>
                <button className="scroll-indicator section-scroll" onClick={scrollToFaq} aria-label="Scroll to FAQ">
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* FAQ Section */}
            <section className="faq-section" ref={faqRef}>
                <Container>
                    <h2 className="section-heading">{t('faqTitle')}</h2>
                    <div className="faq-container">
                        {faqItems.map((item, index) => (
                            <div 
                                className={`faq-item ${openFaqIndex === index ? 'open' : ''}`} 
                                key={index}
                            >
                                <button 
                                    className="faq-question"
                                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                                    aria-expanded={openFaqIndex === index}
                                >
                                    <span>{item.question}</span>
                                    <Icon name={openFaqIndex === index ? 'minus' : 'plus'} />
                                </button>
                                <div className="faq-answer">
                                    <p>{item.answer}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Container>
                <button className="scroll-indicator section-scroll" onClick={scrollToFinalCta} aria-label="Scroll to final call to action">
                    <Icon name="chevron down" />
                </button>
            </section>

            {/* Trusted By Section */}
            <section className="trusted-by-section">
                <Container>
                    <h2 className="section-heading">{t('trustedByTitle')}</h2>
                    <div className="trusted-by-logos">
                        <img src="/Logos/fondation-utc-innovation.png" alt="Fondation UTC pour l'Innovation" />
                        <img src="/Logos/modal.png" alt="Modal" />
                        <img src="/Logos/pepite-sorbonne-universite.jpeg" alt="Pepite Sorbonne Université" />
                        <img src="/Logos/utc.png" alt="UTC" />
                        <img src="/Logos/salad.webp" alt="Salad" />
                    </div>
                </Container>
            </section>

            {/* Final CTA Section */}
            <section className="final-cta-section" ref={finalCtaRef}>
                <Container>
                    <h2 className="final-cta-title">
                        {t('finalCtaTitle')}
                    </h2>
                    <p className="final-cta-subtitle">
                        {t('finalCtaSubtitle')}
                    </p>
                    <button className="cta-primary large" onClick={handleStartAdventure}>
                        <Icon name="play" />
                        {t('ctaPrimary')}
                    </button>
                </Container>
            </section>
        </div>
    )
}

export default HomePage
